/**
 * TeamHQ - header (record, PF/PA, seed) + roster grouped by position
 * + schedule strip + season totals + Set Lineup CTA.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Calendar, Activity, ChevronRight } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";
import ManagerName from "../components/ManagerName";
import { PlayerHeadshot } from "../components/PlayerHeadshot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POS_ORDER = ["QB", "RB", "WR", "TE", "K", "DEF"];

export default function TeamHQ() {
  const { id: leagueId, teamId } = useParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [season, setSeason] = useState(null);
  const [scores, setScores] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!teamId) return;
    setLoadError(null);
    // Independent fetches - page-blocking reads surface errors; optional reads degrade gracefully.
    // `team` is the only hard requirement to render the page.
    axios.get(`${API}/teams/${teamId}`, { headers: authHeaders() })
      .then((t) => setTeam(t.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || e?.message || "Failed to load team"));
    axios.get(`${API}/teams/${teamId}/roster`, { headers: authHeaders() })
      .then((r) => setRoster(r.data || []))
      .catch((e) => setLoadError(e?.response?.data?.detail || e?.message || "Failed to load roster"));
    axios.get(`${API}/teams/${teamId}/schedule`, { headers: authHeaders() })
      .then((sc) => setSchedule(sc.data || []))
      .catch((_e) => setSchedule([]));
    axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() })
      .then((s) => setSeason(s.data))
      .catch((_e) => setSeason(null));
    axios.get(`${API}/teams/${teamId}/scores`, { headers: authHeaders() })
      .then((fs) => setScores(fs.data || []))
      .catch((_e) => setScores([]));
  }, [teamId, leagueId]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  if (!team) {
    if (loadError) {
      return (
        <SeasonShell>
          <div className="py-20 text-center" data-testid="thq-load-error">
            <p className="font-display font-black uppercase text-xl text-[#FF4444] mb-2">Could not load team</p>
            <p className={`font-body text-sm ${muted} mb-4`}>{loadError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white text-sm font-display font-bold uppercase tracking-wide"
              data-testid="thq-retry"
            >Retry</button>
          </div>
        </SeasonShell>
      );
    }
    return <SeasonShell><div className="py-20 text-center"><span className="font-display font-black uppercase text-2xl text-[#FF6B00]">Loading team…</span></div></SeasonShell>;
  }

  const grouped = {};
  for (const r of roster) {
    const pos = r.player?.position || "-";
    if (!grouped[pos]) grouped[pos] = [];
    grouped[pos].push(r);
  }
  for (const pos of Object.keys(grouped)) {
    grouped[pos].sort((a, b) => (b.player?.projected_points || 0) - (a.player?.projected_points || 0));
  }

  const winPct = team.wins + team.losses + team.ties > 0
    ? (team.wins + team.ties * 0.5) / (team.wins + team.losses + team.ties)
    : 0;

  // Bench Regret aggregation across all completed weeks
  const finalScores = scores.filter((s) => (s.total_points ?? 0) > 0 || (s.bench_points ?? 0) > 0);
  const totalBench = finalScores.reduce((a, s) => a + (s.bench_points || 0), 0);
  const totalLeftOnBench = finalScores.reduce((a, s) =>
    a + Math.max(0, (s.optimal_points || 0) - (s.total_points || 0)), 0);
  const lastWeekScore = finalScores.length ? finalScores[finalScores.length - 1] : null;
  const lastWeekRegret = lastWeekScore
    ? Math.max(0, (lastWeekScore.optimal_points || 0) - (lastWeekScore.total_points || 0))
    : 0;

  return (
    <SeasonShell>
      {/* Header */}
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-6 mb-5`} data-testid="thq-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-1">
              Team HQ · Season {season?.season_year || ""}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              <ManagerName name={team.team_name} isBot={team.is_bot} iconSize={20} testId={`thq-team-name-${teamId}`} />
            </h1>
            <div className={`mt-1.5 text-sm ${muted}`}>
              {team.wins}-{team.losses}-{team.ties} · {(winPct * 100).toFixed(0)}% · PF {Math.round(team.points_for)} · PA {Math.round(team.points_against)}
            </div>
          </div>
          {season && (
            <button
              onClick={() => navigate(`/league/${leagueId}/team/${teamId}/lineup/${season.current_week}`)}
              className="px-5 py-3 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase tracking-wide text-sm transition-all shadow-lg shadow-[#FF6B00]/20"
              data-testid="thq-set-lineup"
            >
              Set Week {season.current_week} Lineup →
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat label="FAAB" value={`$${team.faab_remaining ?? "-"}`} />
          <Stat label="Waiver" value={team.waiver_priority ? `#${team.waiver_priority}` : "-"} />
          <Stat label="Playoff Seed" value={team.playoff_seed || "-"} />
          <Stat label="Roster" value={roster.length} />
        </div>

        {/* Bench Regret surface (Spec §"4 Enhancements") */}
        {finalScores.length > 0 && (
          <div
            className="mt-4 px-4 py-3 rounded-xl border border-[#FF6B00]/30 bg-[#FF6B00]/5 flex items-center justify-between gap-4 flex-wrap"
            data-testid="thq-bench-regret"
          >
            <div>
              <div className={`text-[10px] uppercase tracking-widest font-display font-bold ${muted}`}>Bench Regret</div>
              {lastWeekRegret > 0.5 ? (
                <p className="font-body text-sm">
                  You left <span className="font-display font-black text-[#FF6B00] text-base">{lastWeekRegret.toFixed(1)}</span> points on the bench in Week {lastWeekScore.week}.
                </p>
              ) : finalScores.length > 0 && (
                <p className="font-body text-sm">
                  Optimal lineup played in Week {lastWeekScore.week}. Coach of the week.
                </p>
              )}
            </div>
            <div className={`text-xs ${muted}`} data-testid="thq-bench-regret-totals">
              Season total bench points: <span className="text-[#F5F5F5] font-bold">{totalBench.toFixed(1)}</span>
              <span className="mx-2">·</span>
              Left on bench (vs optimal): <span className="text-[#FF6B00] font-bold">{totalLeftOnBench.toFixed(1)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ROSTER */}
        <div className={`lg:col-span-2 rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="thq-roster">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm mb-4">Roster</h2>
          <div className="space-y-4">
            {POS_ORDER.map((pos) => {
              const list = grouped[pos] || [];
              if (list.length === 0) return null;
              return (
                <div key={pos}>
                  <div className={`text-[10px] uppercase tracking-widest font-display font-bold mb-2 ${muted}`}>{pos}</div>
                  <div className="space-y-1.5">
                    {list.map((r) => (
                      <div
                        key={r.id}
                        className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? "bg-[#252525]" : "bg-[#F5F5F5]"} hover:bg-[#FF6B00]/10 transition-all`}
                        data-testid={`thq-roster-player-${r.player.id}`}
                      >
                        <PlayerHeadshot playerId={r.player.id} position={r.player.position} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold uppercase text-xs tracking-wide truncate">{r.player.name}</div>
                          <div className={`text-[10px] ${muted}`}>
                            {r.player.position} · {r.player.nfl_team} · BYE {r.player.bye_week || "-"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-[10px] uppercase tracking-wider ${muted}`}>Proj</div>
                          <div className="font-display font-black text-sm">{Math.round(r.player.projected_points || 0)}</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-[10px] uppercase tracking-wider ${muted}`}>Paid</div>
                          <div className="font-display font-black text-sm text-[#00C853]">${r.winning_bid}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SCHEDULE */}
        <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="thq-schedule">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-[#FF6B00]" /> Schedule
          </h2>
          <div className="space-y-1.5 max-h-[640px] overflow-y-auto pr-1">
            {schedule.map((m) => {
              const isCur = season?.current_week === m.week;
              const win = m.status === "FINAL" && m.my_score > m.opp_score;
              const loss = m.status === "FINAL" && m.my_score < m.opp_score;
              return (
                <div
                  key={m.matchup_id}
                  className={`flex items-center justify-between p-2 rounded-lg text-xs ${
                    isCur ? "bg-[#FF6B00]/10 ring-1 ring-[#FF6B00]" :
                    isDark ? "bg-[#252525]" : "bg-[#F5F5F5]"
                  }`}
                  data-testid={`thq-schedule-week-${m.week}`}
                >
                  <span className="w-12 text-[10px] uppercase tracking-wider text-[#FF6B00] font-bold">W{m.week}</span>
                  <span className="flex-1 truncate inline-flex items-center gap-1">
                    <span>{m.is_home ? "vs" : "@"}</span>
                    <ManagerName name={m.opponent_team_name} isBot={m.opponent_is_bot} iconSize={10} testId={`thq-opp-${m.week}`} />
                  </span>
                  <span className={`text-right font-display font-bold ${win ? "text-[#00C853]" : loss ? "text-red-400" : muted}`}>
                    {m.status === "FINAL"
                      ? `${m.my_score.toFixed(1)} - ${m.opp_score.toFixed(1)}`
                      : `${(m.my_projected || 0).toFixed(1)} proj`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SeasonShell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A0A0A0] font-display font-bold">{label}</div>
      <div className="font-display font-black text-2xl text-[#FF6B00]">{value}</div>
    </div>
  );
}
